import { Collection } from 'firestorter';

import BaseDocument from '../../Model/BaseDocument';
import Collections from '../../utils/collections';

class MealPlanView extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.MEAL_PLAN_VIEW}/${id}`, opts);
  }

  get name() {
    return this.data.mealPlanName;
  }

  get mealTimesSummary() {
    return this.data.mealTimesSummary || [];
  }

  get macroAverages() {
    return this.data.macroAverages;
  }

  get allergenTags() {
    return this.data.allergenTags || [];
  }

  get isArchived() {
    return !!this.data.isArchived;
  }

  get lastUpdated() {
    return this.data.lastUpdated;
  }

  get numberOfAssignments() {
    return this.data.numberOfAssignments;
  }

  get numberOfMealTimes() {
    return this.data.numberOfMealTimes;
  }

  get meals() {
    return this.data.numberOfMeals;
  }

  get type() {
    return this.data.type;
  }

  get isPublic() {
    return !!this.data.isPublic;
  }

  get originalMealPlan() {
    return this.data.originalMealPlan || '';
  }

  get isAutomationTemplate() {
    return !!this.data.isAutomationTemplate;
  }

  static async getMealPlanView(mealPlanViewId) {
    const mealPlanViewDoc = new MealPlanView(mealPlanViewId);
    await mealPlanViewDoc.init();
    return mealPlanViewDoc.exists ? mealPlanViewDoc : null;
  }

  static async getMealPlanViewsByCoach(coach, fetchArchivedPlans) {
    const mealPlanViewsCollection = new Collection(Collections.MEAL_PLAN_VIEW, {
      createDocument: ({ id }, opts) => new MealPlanView(id, { ...opts, disableObserverRefCount: true }),
      query: (ref) => {
        let query = ref.where('coach', '==', coach);
        if (!fetchArchivedPlans) {
          query = query.where('isArchived', '==', false);
        }
        return query;
      },
    });
    await mealPlanViewsCollection.fetch();
    return mealPlanViewsCollection;
  }

  static async getPublicMealPlanViews() {
    const mealPlanViewsCollection = new Collection(Collections.MEAL_PLAN_VIEW, {
      createDocument: ({ id }, opts) => new MealPlanView(id, { ...opts, disableObserverRefCount: true }),
      query: (ref) => ref.where('isPublic', '==', true)
        .where('isArchived', '==', false),
    });
    await mealPlanViewsCollection.fetch();
    return mealPlanViewsCollection;
  }

  /**
   * The coach's meal plans followed by the public ones, minus the public originals the coach already
   * copied (`originalMealPlan`), anything listed twice and the automation template.
   *
   * @param {MealPlanView[]} coachMealPlanViews
   * @param {MealPlanView[]} publicMealPlanViews
   * @returns {MealPlanView[]}
   */
  static combineCoachAndPublic(coachMealPlanViews, publicMealPlanViews) {
    const copiedOriginalIds = new Set(coachMealPlanViews.map(({ originalMealPlan }) => originalMealPlan));
    const coachIds = new Set(coachMealPlanViews.map(({ id }) => id));

    const uncopiedPublicMealPlanViews = publicMealPlanViews.filter(
      ({ id }) => !copiedOriginalIds.has(id) && !coachIds.has(id),
    );

    return [...coachMealPlanViews, ...uncopiedPublicMealPlanViews].filter(
      ({ isAutomationTemplate }) => !isAutomationTemplate,
    );
  }
}

export default MealPlanView;
